import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { showNotification } from '@epi-actions/overlays';
import emptyApi from '@epi-api/emptyApi';
import { get, post } from '@epi-helpers/FetchHelpers';
import config from '@epi-config';
import {
  BenefitGroupsResponse,
  IBenefitDefaultSettings,
  IBenefitDefaultSettingsResponse,
  IBenefitGroup,
  ICreateBenefitGroupForm
} from '@epi-models/pages/BenefitGroups';
import { CREATE_BENEFIT_GROUP_API_TAG } from '@epi-constants/benefitGroups';

export const getBenefitGroups = async (): Promise<BenefitGroupsResponse> =>
  get<BenefitGroupsResponse>(`${config.apiHost}/api/v2/company-benefit-groups`);

export const getDefaultBenefitGroupsSettings =
  async (): Promise<IBenefitDefaultSettingsResponse> =>
    get<IBenefitDefaultSettingsResponse>(
      `${config.apiHost}/api/v2/company-benefit-groups/defaultSettings`
    );

export const createBenefitGroup = async (
  payload: ICreateBenefitGroupForm
): Promise<{ data?: number; error?: any }> => {
  try {
    const response: number | undefined = await post(
      `${config.apiHost}/api/v2/company-benefit-groups`,
      payload
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};

export const benefitGroupsApi = emptyApi.injectEndpoints({
  endpoints: build => ({
    getBenefitGroups: build.query<IBenefitGroup[] | [], void>({
      async queryFn(_arg, api) {
        try {
          const data = await getBenefitGroups();
          const items = data?.items.map(item => {
            const benefitSettings = Object.entries(item.benefitSettings).map(
              ([benefitType, { maxAmountPerPeriod }]) => ({
                benefitType,
                maxAmountPerPeriod
              })
            );

            return {
              ...item,
              benefitSettings
            };
          });
          return { data: items ?? [] };
        } catch (error) {
          api.dispatch(
            showNotification({ message: 'messages.error' }, 'error')
          );
          return { error: error as FetchBaseQueryError };
        }
      },
      keepUnusedDataFor: 1800,
      providesTags: [CREATE_BENEFIT_GROUP_API_TAG]
    }),
    getDefaultBenefitGroupsSettings: build.query<IBenefitDefaultSettings, void>(
      {
        async queryFn(_arg, api) {
          try {
            const data = await getDefaultBenefitGroupsSettings();
            const contractedBenefits = data?.contractedBenefits
              ? Object.entries(data?.contractedBenefits).map(
                  ([benefitType, values]) => ({
                    benefitType,
                    ...values
                  })
                )
              : [];
            return {
              data: {
                lunchGlobalSettings: data.lunchGlobalSettings,
                contractedBenefits
              } as IBenefitDefaultSettings
            };
          } catch (error) {
            api.dispatch(
              showNotification({ message: 'messages.error' }, 'error')
            );
            return { error: error as FetchBaseQueryError };
          }
        },
        keepUnusedDataFor: 1800
      }
    ),
    createBenefitGroup: build.mutation<
      number,
      { payload: ICreateBenefitGroupForm }
    >({
      queryFn: ({ payload }) => createBenefitGroup(payload),
      invalidatesTags: [CREATE_BENEFIT_GROUP_API_TAG]
    })
  })
});
export const {
  useGetBenefitGroupsQuery,
  useGetDefaultBenefitGroupsSettingsQuery,
  useCreateBenefitGroupMutation
} = benefitGroupsApi;
